import type { GroundPoint } from "./store"

const EARTH_RADIUS_KM = 6371
const toRad = (deg: number) => (deg * Math.PI) / 180

/** Great-circle distance between two points, in kilometres. */
export function haversineKm(a: GroundPoint, b: GroundPoint) {
  const dLat = toRad(b.lat - a.lat)
  const dLng = toRad(b.lng - a.lng)
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.sqrt(h))
}

/** Roads run about a quarter longer than the crow flies in a city. */
const ROAD_FACTOR = 1.25
/** A metro drive averages about this, lights and all. */
const METRO_KMH = 32
/** Beyond the metro band the drive is highway. */
const METRO_KM = 30
const HIGHWAY_KMH = 85

/**
 * A drive estimate from the straight line — no routing call is made, so the
 * label says so with "≈" and "~". Good enough to know whether it is a morning
 * or an afternoon; the map link carries the real route. The origin can be
 * changed to an office in another state, so a long drive reads in hours and
 * whole kilometres rather than a thousand minutes.
 */
export function driveEstimate(from: GroundPoint, to: GroundPoint) {
  const km = haversineKm(from, to) * ROAD_FACTOR
  const metro = Math.min(km, METRO_KM)
  const highway = Math.max(km - METRO_KM, 0)
  const min = Math.max(
    1,
    Math.round((metro / METRO_KMH + highway / HIGHWAY_KMH) * 60),
  )
  const distance = km >= 100 ? `${Math.round(km)} km` : `${km.toFixed(1)} km`
  const time =
    min >= 90
      ? `${Math.floor(min / 60)} h${min % 60 ? ` ${min % 60} min` : ""}`
      : `${min} min`
  return { km, min, label: `≈${distance} · ~${time} drive` }
}

/** The one escape hatch out of the ground: the same drive in the user's map app. */
export function mapsDirectionsUrl(from: GroundPoint, to: GroundPoint) {
  const params = new URLSearchParams({
    api: "1",
    origin: `${from.lat},${from.lng}`,
    destination: `${to.lat},${to.lng}`,
    travelmode: "driving",
  })
  return `https://www.google.com/maps/dir/?${params.toString()}`
}
